import Honeybadger from 'honeybadger-js'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useDashboardData } from 'hooks/DashboardDataContext'
import { CardConfig, CardId, HEHM_CARD_REGISTRY, NON_HEHM_CARD_REGISTRY } from 'pages/Dashboard/cardRegistry'
import { END_DROP_ZONE_ID } from 'pages/Dashboard/EndDropZone'
import Api from 'services/api'

type StoredLayout = { added: CardId[]; removed: CardId[] }

const parseLayout = (raw: string | null | undefined, registry: Record<string, CardConfig>): StoredLayout | null => {
  if (!raw) return null

  try {
    const { added, removed } = JSON.parse(raw) as StoredLayout
    const isCardId = (id: unknown): id is CardId => typeof id === 'string' && id in registry

    return {
      added: added.filter(isCardId),
      removed: removed.filter(isCardId)
    }
  } catch {
    return null
  }
}

const STORAGE_KEY = 'dashboardLayout'

const writeToStorage = (layout: StoredLayout) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(layout))

const useDashboardLayout = () => {
  const { enabledFeatures, isEnabledFeaturesLoaded } = useDashboardData()
  const isHehmEnabled = enabledFeatures.includes('hehm')
  const cardRegistry = isHehmEnabled ? HEHM_CARD_REGISTRY : NON_HEHM_CARD_REGISTRY
  const [persistedAddedCards, setPersistedAddedCards] = useState<CardId[]>([])
  const [persistedRemovedCards, setPersistedRemovedCards] = useState<CardId[]>([])
  const [isLayoutLoaded, setIsLayoutLoaded] = useState(false)
  const [isCustomizing, setIsCustomizing] = useState(false)
  const [draftAddedCards, setDraftAddedCards] = useState<CardId[]>([])
  const [draftRemovedCards, setDraftRemovedCards] = useState<CardId[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isEnabledFeaturesLoaded) return

    if (!isHehmEnabled) {
      setPersistedAddedCards(Object.keys(NON_HEHM_CARD_REGISTRY) as CardId[])
      setPersistedRemovedCards([])
      setIsLayoutLoaded(true)
      return
    }

    const cached = parseLayout(localStorage.getItem(STORAGE_KEY), cardRegistry)

    if (cached) {
      setPersistedAddedCards(cached.added)
      setPersistedRemovedCards(cached.removed)
      setIsLayoutLoaded(true)
    }

    Api.DashboardLayout.get()
      .then((res: { dashboardLayout: string | null }) => {
        const persisted = parseLayout(res.dashboardLayout, cardRegistry)

        if (persisted) {
          setPersistedAddedCards(persisted.added)
          setPersistedRemovedCards(persisted.removed)
          writeToStorage(persisted)
        } else if (res.dashboardLayout) {
          Honeybadger.notify('Malformed dashboardLayout payload from API', {
            context: { dashboardLayout: res.dashboardLayout }
          })
        }
      })
      .finally(() => {
        setIsLayoutLoaded(true)
      })
  }, [isEnabledFeaturesLoaded, isHehmEnabled, cardRegistry])

  const addedCards = useMemo<CardId[]>(() => {
    const baseAdded = isCustomizing ? draftAddedCards : persistedAddedCards
    const baseRemoved = isCustomizing ? draftRemovedCards : persistedRemovedCards
    const registryKeys = Object.keys(cardRegistry) as CardId[]
    const registryKeySet = new Set(registryKeys)
    const removedSet = new Set(baseRemoved)
    const present = baseAdded.filter((id) => registryKeySet.has(id) && !removedSet.has(id))
    const known = new Set(baseAdded)
    const missing = registryKeys.filter((id) => !known.has(id) && !removedSet.has(id))

    return [...present, ...missing]
  }, [persistedAddedCards, persistedRemovedCards, draftAddedCards, draftRemovedCards, isCustomizing, cardRegistry])

  const removedCards = useMemo<CardId[]>(() => {
    const baseRemoved = isCustomizing ? draftRemovedCards : persistedRemovedCards
    const removedSet = new Set(baseRemoved)

    return (Object.keys(cardRegistry) as CardId[]).filter((id) => removedSet.has(id))
  }, [persistedRemovedCards, draftRemovedCards, isCustomizing, cardRegistry])

  const startCustomize = useCallback(() => {
    setDraftAddedCards(addedCards)
    setDraftRemovedCards(removedCards)
    setIsCustomizing(true)
  }, [addedCards, removedCards])

  const reorder = useCallback((activeId: CardId, overId: CardId | typeof END_DROP_ZONE_ID) => {
    setDraftAddedCards((current) => {
      if (activeId === overId) return current

      const next = current.filter((id) => id !== activeId)

      if (overId === END_DROP_ZONE_ID) {
        next.push(activeId)

        return next
      }

      const insertAt = next.indexOf(overId)

      if (insertAt === -1) return current

      next.splice(insertAt, 0, activeId)

      return next
    })
  }, [])

  const removeCard = useCallback((id: CardId) => {
    setDraftAddedCards((current) => current.filter((cardId) => cardId !== id))
    setDraftRemovedCards((current) => (current.includes(id) ? current : [...current, id]))
  }, [])

  const addCard = useCallback((id: CardId) => {
    setDraftRemovedCards((current) => current.filter((cardId) => cardId !== id))
    setDraftAddedCards((current) => (current.includes(id) ? current : [id, ...current]))
  }, [])

  const save = useCallback(async () => {
    setIsSaving(true)

    const payload: StoredLayout = { added: draftAddedCards, removed: draftRemovedCards }

    try {
      await Api.DashboardLayout.update(JSON.stringify(payload))
      setPersistedAddedCards(draftAddedCards)
      setPersistedRemovedCards(draftRemovedCards)
      writeToStorage(payload)
      setIsCustomizing(false)
    } finally {
      setIsSaving(false)
    }
  }, [draftAddedCards, draftRemovedCards])

  const cancel = useCallback(() => {
    setIsCustomizing(false)
  }, [])

  return {
    addedCards,
    removedCards,
    isLayoutLoaded,
    isCustomizing,
    isSaving,
    startCustomize,
    reorder,
    removeCard,
    addCard,
    save,
    cancel,
    enabledFeatures,
    cardRegistry: cardRegistry as Record<CardId, CardConfig>
  }
}

export default useDashboardLayout
